import type { SessionStatus } from '@diana/contracts';
import { ChevronRight, LoaderCircle, type LucideIcon } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode } from 'react';

export type TypographyVariant =
  | 'brand'
  | 'display'
  | 'counter'
  | 'page-title'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'body-large'
  | 'body'
  | 'small'
  | 'label'
  | 'eyebrow'
  | 'caption'
  | 'helper';

type TypographyProps = HTMLAttributes<HTMLElement> & {
  as?: 'p' | 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4';
  variant?: TypographyVariant;
};

export function Typography({
  as: Element = 'p',
  variant = 'body',
  className,
  ...props
}: TypographyProps) {
  return (
    <Element
      className={['dui-type', `dui-type--${variant}`, className].filter(Boolean).join(' ')}
      {...props}
    />
  );
}

export type ButtonVariant =
  'primary' | 'secondary' | 'outline' | 'quiet' | 'destructive' | 'text' | 'text-danger';
export type ButtonSize = 'small' | 'medium' | 'large';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leadingIcon?: LucideIcon;
  trailingIcon?: LucideIcon;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'medium',
    loading = false,
    leadingIcon: LeadingIcon,
    trailingIcon: TrailingIcon,
    className,
    children,
    disabled,
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      className={['dui-button', `dui-button--${variant}`, `dui-button--${size}`, className]
        .filter(Boolean)
        .join(' ')}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      <span className="dui-button__content">
        {loading ? (
          <LoaderCircle className="dui-button__spinner" size={15} aria-hidden="true" />
        ) : LeadingIcon ? (
          <LeadingIcon size={15} aria-hidden="true" />
        ) : null}
        <span>{children}</span>
        {!loading && TrailingIcon ? <TrailingIcon size={15} aria-hidden="true" /> : null}
      </span>
    </button>
  );
});

type IconButtonProps = Omit<ButtonProps, 'children'> & {
  icon: LucideIcon;
  label: string;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon: Icon, label, className, ...props },
  ref,
) {
  return (
    <Button
      ref={ref}
      className={['dui-icon-button', className].filter(Boolean).join(' ')}
      aria-label={label}
      {...props}
    >
      <Icon size={16} aria-hidden="true" />
    </Button>
  );
});

export type StatusTone = 'neutral' | 'success' | 'warning' | 'danger';

export function Badge({ tone = 'neutral', children }: { tone?: StatusTone; children: ReactNode }) {
  return <span className={`dui-badge dui-badge--${tone}`}>{children}</span>;
}

export function StatusDot({
  tone = 'neutral',
  children,
}: {
  tone?: StatusTone;
  children: ReactNode;
}) {
  return (
    <span className={`dui-status-dot dui-status-dot--${tone}`}>
      <span aria-hidden="true" />
      {children}
    </span>
  );
}

export function Divider({
  orientation = 'horizontal',
}: {
  orientation?: 'horizontal' | 'vertical';
}) {
  return (
    <span
      className={`dui-divider dui-divider--${orientation}`}
      role="separator"
      aria-orientation={orientation}
    />
  );
}

export type StageTone =
  'new' | 'discussion' | 'scheduled' | 'editing' | 'delivered' | 'canceled' | 'archived';

export function stageTone(status: SessionStatus): StageTone {
  const tones = {
    NewRequest: 'new',
    InDiscussion: 'discussion',
    Scheduled: 'scheduled',
    Editing: 'editing',
    Delivered: 'delivered',
    Canceled: 'canceled',
  } as const satisfies Record<SessionStatus, StageTone>;
  return tones[status];
}

export function StageTag({
  status,
  dot = false,
  children,
}: {
  status: SessionStatus;
  dot?: boolean;
  children: ReactNode;
}) {
  return (
    <span className={`dui-stage-tag dui-stage-tag--${stageTone(status)}`}>
      {dot ? <span className="dui-stage-dot" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

export const Chip = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { selected?: boolean; status?: SessionStatus }
>(function Chip({ selected = false, status, className, children, type = 'button', ...props }, ref) {
  return (
    <button
      {...props}
      ref={ref}
      type={type}
      aria-pressed={selected}
      className={['dui-chip', status && `dui-stage-tag--${stageTone(status)}`, className]
        .filter(Boolean)
        .join(' ')}
    >
      {status ? <span className="dui-stage-dot" aria-hidden="true" /> : null}
      {children}
    </button>
  );
});

export function GroupedRows({ children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...props} className={['dui-grouped-rows', props.className].filter(Boolean).join(' ')}>
      {children}
    </div>
  );
}

export function GroupedRow({
  label,
  value,
  onClick,
  disabled,
}: {
  label: string;
  value: ReactNode;
  onClick?(): void;
  disabled?: boolean;
}) {
  const content = (
    <>
      <span className="dui-grouped-row__label">{label}</span>
      <span className="dui-grouped-row__value">{value}</span>
      {onClick ? <ChevronRight size={16} aria-hidden="true" /> : null}
    </>
  );
  return onClick ? (
    <button type="button" className="dui-grouped-row" onClick={onClick} disabled={disabled}>
      {content}
    </button>
  ) : (
    <div className="dui-grouped-row">{content}</div>
  );
}
